import { TituloOrdenable } from '@/components/dominio/TituloOrdenable';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useKpisObras, useKpisPartidas, usePartidaDetalle } from '@/hooks/useAvanceMatriz';
import {
  type CeldaCostosM2Export,
  type ColumnaCostosM2Export,
  type FilaCostosM2Export,
  exportarCostosM2Excel,
} from '@/lib/exportarCostosM2Excel';
import { type OrdenColumna, ordenarHermanos, siguienteOrden } from '@/lib/ordenColumna';
import type { ResumenKpiObra } from '@adelante/shared';
import { CaretDown, CaretRight, CircleNotch, DownloadSimple, Info, X } from '@phosphor-icons/react';
import { type ReactElement, useMemo, useState } from 'react';

function fmtMonto(v: number): string {
  if (!v) return '—';
  const neg = v < 0;
  const s = Math.round(Math.abs(v))
    .toLocaleString('es-CR', { maximumFractionDigits: 0 })
    .replace(/,/g, ' ');
  return `${neg ? '-' : ''}₡${s}`;
}
function fmtPct(v: number): string {
  return `${(v * 100).toLocaleString('es-CR', { maximumFractionDigits: 1 })}%`;
}
function fmtM2(v: number): string {
  return v.toLocaleString('es-CR', { maximumFractionDigits: 1 });
}

/** Acumulador de métricas (sumables). pct y m² no se suman directo: se derivan. */
interface Agg {
  area: number;
  m2c: number;
  presupuesto: number;
  costoDir: number;
  costoAvance: number;
}
const aggVacio = (): Agg => ({
  area: 0,
  m2c: 0,
  presupuesto: 0,
  costoDir: 0,
  costoAvance: 0,
});

const COLUMNAS_EXPORT: ColumnaCostosM2Export[] = [
  { etiqueta: 'Presupuesto', fmt: 'moneda' },
  { etiqueta: '% Avance', fmt: 'porcentaje' },
  { etiqueta: 'Costo Avance', fmt: 'moneda' },
  { etiqueta: 'Costo Directo', fmt: 'moneda' },
  { etiqueta: 'Desviación', fmt: 'moneda' },
  { etiqueta: 'm² Total', fmt: 'numero' },
  { etiqueta: 'm² Const.', fmt: 'numero' },
  { etiqueta: 'Costo/m²', fmt: 'moneda' },
];

/** Celdas de export en el mismo orden que COLUMNAS_EXPORT / celdasMetricas. */
function celdasExport(
  presupuesto: number,
  pct: number,
  costoAvance: number,
  costoDir: number,
  desviacion: number,
  area: number,
  m2c: number,
): CeldaCostosM2Export[] {
  const costoXm2 = m2c > 0 ? costoDir / m2c : 0;
  return [
    { valor: presupuesto },
    { valor: pct },
    { valor: costoAvance },
    { valor: costoDir },
    { valor: desviacion },
    { valor: area },
    { valor: m2c },
    { valor: costoXm2 },
  ];
}
function sumar(a: Agg, k: ResumenKpiObra): Agg {
  a.area += k.areaM2Total;
  a.m2c += k.m2Construidos;
  a.presupuesto += k.presupuesto;
  a.costoDir += k.costoDirecto;
  a.costoAvance += k.costoAvance;
  return a;
}

interface NodoObra {
  tipo: 'obra';
  kpi: ResumenKpiObra;
}
interface NodoBloque {
  tipo: 'bloque';
  bloque: string;
  key: string;
  agg: Agg;
  obras: NodoObra[];
}
interface NodoProyecto {
  tipo: 'proyecto';
  proyecto: string;
  agg: Agg;
  bloques: NodoBloque[];
}

/** Modal con los Dim.Códigos consumidos en una partida. */
function ModalPartida({
  worksNo,
  taskNo,
  tareaDisplay,
  onClose,
}: {
  worksNo: string;
  taskNo: string;
  tareaDisplay: string | null;
  onClose: () => void;
}): ReactElement {
  const { data, isLoading } = usePartidaDetalle(worksNo, taskNo);
  const codigos = data?.data ?? [];
  const total = codigos.reduce((s, c) => s + c.totalCostoLcy, 0);

  return (
    <>
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: backdrop decorativo, cierre por botón X */}
      <div className="fixed inset-0 z-40 bg-black/40" onClick={onClose} aria-hidden="true" />
      <div className="fixed inset-y-0 right-0 z-50 flex w-full max-w-[640px] flex-col bg-zinc-50 shadow-2xl">
        <div className="flex items-center justify-between border-b border-zinc-200 bg-white px-5 py-3">
          <div>
            <div className="text-xs text-muted-foreground">{worksNo} · Dim.Códigos consumidos</div>
            <div className="text-lg font-semibold">{tareaDisplay ?? taskNo}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-2 text-muted-foreground hover:bg-zinc-100"
            title="Cerrar"
          >
            <X className="h-5 w-5" weight="bold" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {isLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <CircleNotch className="h-4 w-4 animate-spin" weight="bold" /> Cargando…
            </div>
          ) : codigos.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Sin consumos registrados en esta partida.
            </p>
          ) : (
            <div className="overflow-x-auto rounded border border-zinc-200 bg-white">
              <table className="w-full text-xs">
                <thead className="border-b bg-zinc-50">
                  <tr className="text-left text-muted-foreground">
                    <th className="px-3 py-1.5">Código</th>
                    <th className="px-3 py-1.5">Descripción</th>
                    <th className="px-3 py-1.5 text-right">Cant</th>
                    <th className="px-3 py-1.5">UM</th>
                    <th className="px-3 py-1.5 text-right">Costo</th>
                  </tr>
                </thead>
                <tbody>
                  {codigos.map((c) => (
                    <tr
                      key={c.consolidatedCode ?? Math.random()}
                      className="border-b border-zinc-100"
                    >
                      <td className="px-3 py-1.5 font-mono text-[10px]">
                        {c.consolidatedCode ?? '—'}
                      </td>
                      <td className="px-3 py-1.5">
                        <div className="truncate max-w-[240px]">{c.description ?? '—'}</div>
                        {c.itemCategoryCode && (
                          <div className="text-[10px] text-muted-foreground">
                            {c.tipoCodigo} · {c.itemCategoryCode}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums">
                        {c.totalQuantity.toLocaleString('es-CR', { maximumFractionDigits: 1 })}
                      </td>
                      <td className="px-3 py-1.5 text-[10px]">{c.unidadMedida ?? '—'}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">
                        {fmtMonto(c.totalCostoLcy)}
                      </td>
                    </tr>
                  ))}
                  <tr className="border-t bg-zinc-50 font-semibold">
                    <td className="px-3 py-1.5" colSpan={4}>
                      Total ({codigos.length} códigos)
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{fmtMonto(total)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

/** Filas de partidas (drill-down) de una obra expandida en Costos/m². */
function FilasPartidasObra({
  worksNo,
  onInfo,
}: {
  worksNo: string;
  onInfo: (worksNo: string, taskNo: string, tareaDisplay: string | null) => void;
}): ReactElement {
  const { data, isLoading } = useKpisPartidas(worksNo);
  if (isLoading) {
    return (
      <TableRow>
        <TableCell colSpan={9} className="bg-zinc-50/50 text-xs text-muted-foreground">
          <div className="flex items-center gap-2 pl-14">
            <CircleNotch className="h-3 w-3 animate-spin" weight="bold" /> Cargando partidas…
          </div>
        </TableCell>
      </TableRow>
    );
  }
  const partidas = (data?.data ?? []).filter(
    (p) => p.costoPresupuesto !== 0 || p.costoReal !== 0 || p.pctAvance !== 0,
  );
  return (
    <>
      {partidas.map((p) => (
        <TableRow key={`PT|${worksNo}|${p.taskNo}`} className="bg-zinc-50/50">
          <TableCell className="sticky left-0 z-10 bg-zinc-50 text-xs">
            <div className="flex items-center gap-1 pl-14">
              <button
                type="button"
                onClick={() => onInfo(worksNo, p.taskNo, p.tareaDisplay)}
                className="rounded-full p-0.5 text-blue-600 hover:bg-blue-100"
                title="Ver Dim.Códigos consumidos"
                aria-label="Detalle de la partida"
              >
                <Info className="h-3.5 w-3.5" weight="bold" />
              </button>
              <span className="font-medium">{p.taskNo}</span>
              {p.tareaDisplay && (
                <span className="ml-1 text-[10px] text-muted-foreground">
                  {p.tareaDisplay.replace(new RegExp(`^${p.taskNo}\\s*-?\\s*`), '')}
                </span>
              )}
            </div>
          </TableCell>
          <TableCell className="text-right text-xs tabular-nums">
            {fmtMonto(p.costoPresupuesto)}
          </TableCell>
          <TableCell className="text-right text-xs tabular-nums">{fmtPct(p.pctAvance)}</TableCell>
          <TableCell className="text-right text-xs tabular-nums">
            {fmtMonto(p.costoAvance)}
          </TableCell>
          <TableCell className="text-right text-xs tabular-nums">{fmtMonto(p.costoReal)}</TableCell>
          <TableCell
            className={`text-right text-xs tabular-nums font-medium ${p.desviacion > 0 ? 'text-red-600' : 'text-emerald-700'}`}
          >
            {fmtMonto(p.desviacion)}
          </TableCell>
          <TableCell className="text-right text-xs tabular-nums text-zinc-300">—</TableCell>
          <TableCell className="text-right text-xs tabular-nums text-zinc-300">—</TableCell>
          <TableCell className="text-right text-xs tabular-nums text-zinc-300">—</TableCell>
        </TableRow>
      ))}
    </>
  );
}

export function CostosM2Pantalla() {
  // El corte sale del filtro Períodos global (mes máximo seleccionado);
  // useKpisObras lo deriva internamente. Sin período → estado actual.
  const { data, isLoading, error } = useKpisObras();
  const [expandidos, setExpandidos] = useState<Set<string>>(new Set());
  const [orden, setOrden] = useState<OrdenColumna | null>(null);
  const [modalPartida, setModalPartida] = useState<{
    worksNo: string;
    taskNo: string;
    tareaDisplay: string | null;
  } | null>(null);

  const proyectos = useMemo(() => {
    const filas = data?.data ?? [];
    const proyMap = new Map<string, NodoProyecto>();
    const bloMap = new Map<string, NodoBloque>();

    for (const k of filas) {
      const proy = k.bloque ?? '(sin proyecto)';
      const bloque = k.subBloque ?? '(sin bloque)';
      const bloKey = `${proy}|${bloque}`;
      let nodoP = proyMap.get(proy);
      if (!nodoP) {
        nodoP = { tipo: 'proyecto', proyecto: proy, agg: aggVacio(), bloques: [] };
        proyMap.set(proy, nodoP);
      }
      let nodoB = bloMap.get(bloKey);
      if (!nodoB) {
        nodoB = { tipo: 'bloque', bloque, key: bloKey, agg: aggVacio(), obras: [] };
        bloMap.set(bloKey, nodoB);
        nodoP.bloques.push(nodoB);
      }
      nodoB.obras.push({ tipo: 'obra', kpi: k });
      sumar(nodoB.agg, k);
      sumar(nodoP.agg, k);
    }
    const arr = [...proyMap.values()].sort((a, b) => a.proyecto.localeCompare(b.proyecto));
    for (const p of arr) {
      p.bloques.sort((a, b) => a.bloque.localeCompare(b.bloque));
      for (const b of p.bloques) b.obras.sort((x, y) => x.kpi.worksNo.localeCompare(y.kpi.worksNo));
    }
    return arr;
  }, [data]);

  // Gran total = suma de los agg de todos los proyectos.
  const totalAgg = useMemo(() => {
    const t = aggVacio();
    for (const p of proyectos) {
      t.area += p.agg.area;
      t.m2c += p.agg.m2c;
      t.presupuesto += p.agg.presupuesto;
      t.costoDir += p.agg.costoDir;
      t.costoAvance += p.agg.costoAvance;
    }
    return t;
  }, [proyectos]);

  const toggle = (key: string) =>
    setExpandidos((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const todasKeys = useMemo(() => {
    const k = new Set<string>();
    for (const p of proyectos) {
      k.add(`P|${p.proyecto}`);
      for (const b of p.bloques) k.add(`B|${b.key}`);
    }
    return k;
  }, [proyectos]);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <CircleNotch className="h-4 w-4 animate-spin" weight="bold" />
        Cargando…
      </div>
    );
  }
  if (error) {
    return (
      <Card className="border-destructive">
        <CardContent className="pt-6">
          <p className="text-destructive">Error al cargar: {error.message}</p>
        </CardContent>
      </Card>
    );
  }

  // % avance derivado para grupos: m2 construidos / área.
  const pctDe = (a: Agg) => (a.area > 0 ? a.m2c / a.area : 0);
  const desvDe = (a: Agg) => a.costoDir - a.costoAvance;

  // Valores por columna (mismo orden que COLUMNAS_EXPORT) para ordenar los hermanos de cada nivel.
  const valoresAgg = (a: Agg): (number | null)[] => [
    a.presupuesto || null,
    pctDe(a),
    a.costoAvance || null,
    a.costoDir || null,
    desvDe(a) || null,
    a.area || null,
    a.m2c || null,
    a.m2c > 0 ? a.costoDir / a.m2c : null,
  ];
  const valoresObra = (k: ResumenKpiObra): (number | null)[] => [
    k.presupuesto || null,
    k.pctAvance,
    k.costoAvance || null,
    k.costoDirecto || null,
    k.desviacion || null,
    k.areaM2Total || null,
    k.m2Construidos || null,
    k.m2Construidos > 0 ? k.costoDirecto / k.m2Construidos : null,
  ];
  const proyectosOrd = ordenarHermanos(proyectos, orden, (p) => valoresAgg(p.agg));
  const bloquesOrd = (p: NodoProyecto) =>
    ordenarHermanos(p.bloques, orden, (b) => valoresAgg(b.agg));
  const obrasOrd = (b: NodoBloque) => ordenarHermanos(b.obras, orden, (o) => valoresObra(o.kpi));
  const alOrdenar = (idx: number) => setOrden((actual) => siguienteOrden(actual, idx));

  const celdasMetricas = (
    presupuesto: number,
    pct: number,
    costoAvance: number,
    costoDir: number,
    desviacion: number,
    area: number,
    m2c: number,
  ) => {
    const costoXm2 = m2c > 0 ? costoDir / m2c : 0;
    return (
      <>
        <TableCell className="text-right text-xs tabular-nums">{fmtMonto(presupuesto)}</TableCell>
        <TableCell className="text-right text-xs tabular-nums">{fmtPct(pct)}</TableCell>
        <TableCell className="text-right text-xs tabular-nums">{fmtMonto(costoAvance)}</TableCell>
        <TableCell className="text-right text-xs tabular-nums">{fmtMonto(costoDir)}</TableCell>
        <TableCell
          className={`text-right text-xs tabular-nums font-medium ${desviacion > 0 ? 'text-red-600' : 'text-emerald-700'}`}
        >
          {fmtMonto(desviacion)}
        </TableCell>
        <TableCell className="text-right text-xs tabular-nums text-zinc-400">
          {fmtM2(area)}
        </TableCell>
        <TableCell className="text-right text-xs tabular-nums">{fmtM2(m2c)}</TableCell>
        <TableCell className="text-right text-xs tabular-nums">{fmtMonto(costoXm2)}</TableCell>
      </>
    );
  };

  const filas: ReactElement[] = [];
  for (const p of proyectosOrd) {
    const pKey = `P|${p.proyecto}`;
    const pOpen = expandidos.has(pKey);
    filas.push(
      <TableRow key={pKey} className="bg-emerald-100 font-semibold">
        <TableCell className="sticky left-0 z-10 bg-emerald-100 text-xs">
          <button
            type="button"
            onClick={() => toggle(pKey)}
            className="flex items-center gap-1 hover:underline"
          >
            {pOpen ? (
              <CaretDown className="h-3 w-3" weight="bold" />
            ) : (
              <CaretRight className="h-3 w-3" weight="bold" />
            )}
            {p.proyecto}
          </button>
        </TableCell>
        {celdasMetricas(
          p.agg.presupuesto,
          pctDe(p.agg),
          p.agg.costoAvance,
          p.agg.costoDir,
          desvDe(p.agg),
          p.agg.area,
          p.agg.m2c,
        )}
      </TableRow>,
    );
    if (!pOpen) continue;
    for (const b of bloquesOrd(p)) {
      const bKey = `B|${b.key}`;
      const bOpen = expandidos.has(bKey);
      filas.push(
        <TableRow key={bKey} className="bg-emerald-50 font-medium">
          <TableCell className="sticky left-0 z-10 bg-emerald-50 text-xs">
            <button
              type="button"
              onClick={() => toggle(bKey)}
              className="flex items-center gap-1 pl-4 hover:underline"
            >
              {bOpen ? (
                <CaretDown className="h-3 w-3" weight="bold" />
              ) : (
                <CaretRight className="h-3 w-3" weight="bold" />
              )}
              {b.bloque}
            </button>
          </TableCell>
          {celdasMetricas(
            b.agg.presupuesto,
            pctDe(b.agg),
            b.agg.costoAvance,
            b.agg.costoDir,
            desvDe(b.agg),
            b.agg.area,
            b.agg.m2c,
          )}
        </TableRow>,
      );
      if (!bOpen) continue;
      for (const o of obrasOrd(b)) {
        const k = o.kpi;
        const oKey = `O|${k.worksNo}`;
        const oOpen = expandidos.has(oKey);
        filas.push(
          <TableRow key={oKey}>
            <TableCell className="sticky left-0 z-10 bg-background text-xs">
              <button
                type="button"
                onClick={() => toggle(oKey)}
                className="flex items-start gap-1 pl-9 text-left hover:underline"
              >
                {oOpen ? (
                  <CaretDown className="mt-0.5 h-3 w-3 shrink-0" weight="bold" />
                ) : (
                  <CaretRight className="mt-0.5 h-3 w-3 shrink-0" weight="bold" />
                )}
                <span>
                  <span className="font-medium">{k.worksNo}</span>
                  {k.displayName && k.displayName !== k.worksNo && (
                    <span className="block text-[10px] text-muted-foreground truncate max-w-[200px]">
                      {k.displayName}
                    </span>
                  )}
                </span>
              </button>
            </TableCell>
            {celdasMetricas(
              k.presupuesto,
              k.pctAvance,
              k.costoAvance,
              k.costoDirecto,
              k.desviacion,
              k.areaM2Total,
              k.m2Construidos,
            )}
          </TableRow>,
        );
        if (oOpen)
          filas.push(
            <FilasPartidasObra
              key={`PT|${k.worksNo}`}
              worksNo={k.worksNo}
              onInfo={(w, t, d) => setModalPartida({ worksNo: w, taskNo: t, tareaDisplay: d })}
            />,
          );
      }
    }
  }

  // Exportar a Excel: recorre el árbol COMPLETO (Proyecto→Bloque→Obra), con
  // subtotales por nivel y Gran Total, respetando los filtros globales activos.
  const exportar = () => {
    const filasExport: FilaCostosM2Export[] = [];
    for (const p of proyectosOrd) {
      filasExport.push({
        depth: 0,
        etiqueta: p.proyecto,
        esSubtotal: true,
        celdas: celdasExport(
          p.agg.presupuesto,
          pctDe(p.agg),
          p.agg.costoAvance,
          p.agg.costoDir,
          desvDe(p.agg),
          p.agg.area,
          p.agg.m2c,
        ),
      });
      for (const b of bloquesOrd(p)) {
        filasExport.push({
          depth: 1,
          etiqueta: b.bloque,
          esSubtotal: true,
          celdas: celdasExport(
            b.agg.presupuesto,
            pctDe(b.agg),
            b.agg.costoAvance,
            b.agg.costoDir,
            desvDe(b.agg),
            b.agg.area,
            b.agg.m2c,
          ),
        });
        for (const o of obrasOrd(b)) {
          const k = o.kpi;
          const etiqueta =
            k.displayName && k.displayName !== k.worksNo
              ? `${k.worksNo} — ${k.displayName}`
              : k.worksNo;
          filasExport.push({
            depth: 2,
            etiqueta,
            esSubtotal: false,
            celdas: celdasExport(
              k.presupuesto,
              k.pctAvance,
              k.costoAvance,
              k.costoDirecto,
              k.desviacion,
              k.areaM2Total,
              k.m2Construidos,
            ),
          });
        }
      }
    }
    filasExport.push({
      depth: 0,
      etiqueta: 'Gran Total',
      esSubtotal: false,
      esGranTotal: true,
      celdas: celdasExport(
        totalAgg.presupuesto,
        pctDe(totalAgg),
        totalAgg.costoAvance,
        totalAgg.costoDir,
        desvDe(totalAgg),
        totalAgg.area,
        totalAgg.m2c,
      ),
    });

    exportarCostosM2Excel({
      nombreArchivo: 'costo-vs-teorico',
      tituloHoja: 'Costo vs Teórico',
      encabezadoJerarquia: 'Proyecto / Bloque / Obra',
      columnas: COLUMNAS_EXPORT,
      filas: filasExport,
    });
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Costo vs Teórico</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Por obra: presupuesto, % avance, Costo Avance (teórico = Σ %avance×presupuesto costo por
          partida), costo real directo y Desviación (real − avance). Base directa. Acumulado al mes
          más reciente del filtro Períodos (o al día si no hay período seleccionado). Agrupado por
          Proyecto → Bloque → Obra.
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-3 text-xs">
          <button
            type="button"
            onClick={() => setExpandidos(new Set(todasKeys))}
            className="text-blue-600 hover:underline"
          >
            Expandir todo
          </button>
          <span className="text-zinc-300">|</span>
          <button
            type="button"
            onClick={() => setExpandidos(new Set())}
            className="text-blue-600 hover:underline"
          >
            Colapsar
          </button>
          <span className="ml-auto">
            <button
              type="button"
              onClick={exportar}
              className="inline-flex items-center gap-1 rounded-md border border-emerald-700 bg-white px-2 py-1 font-medium text-emerald-700 hover:bg-emerald-50"
              title="Exportar el reporte a Excel (filtros actuales)"
            >
              <DownloadSimple className="h-3.5 w-3.5" weight="bold" />
              Exportar a Excel
            </button>
          </span>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table wrapperClassName="max-h-[calc(100vh-220px)]">
            <TableHeader>
              <TableRow className="bg-emerald-900 text-white hover:bg-emerald-900">
                <TableHead className="sticky left-0 top-0 z-30 bg-emerald-900 text-white min-w-[200px]">
                  Proyecto / Bloque / Obra
                </TableHead>
                <TableHead className="sticky top-0 z-20 bg-emerald-900 text-right text-white">
                  <TituloOrdenable
                    titulo="Presupuesto"
                    idx={0}
                    orden={orden}
                    onOrdenar={alOrdenar}
                  />
                </TableHead>
                <TableHead className="sticky top-0 z-20 bg-emerald-900 text-right text-white">
                  <TituloOrdenable titulo="% Avance" idx={1} orden={orden} onOrdenar={alOrdenar} />
                </TableHead>
                <TableHead className="sticky top-0 z-20 bg-emerald-900 text-right text-white">
                  <TituloOrdenable
                    titulo="Costo Avance"
                    idx={2}
                    orden={orden}
                    onOrdenar={alOrdenar}
                  />
                </TableHead>
                <TableHead className="sticky top-0 z-20 bg-emerald-900 text-right text-white">
                  <TituloOrdenable
                    titulo="Costo Directo"
                    idx={3}
                    orden={orden}
                    onOrdenar={alOrdenar}
                  />
                </TableHead>
                <TableHead className="sticky top-0 z-20 bg-emerald-900 text-right text-white">
                  <TituloOrdenable
                    titulo="Desviación"
                    idx={4}
                    orden={orden}
                    onOrdenar={alOrdenar}
                  />
                </TableHead>
                <TableHead className="sticky top-0 z-20 bg-emerald-900 text-right text-white">
                  <TituloOrdenable titulo="m² Total" idx={5} orden={orden} onOrdenar={alOrdenar} />
                </TableHead>
                <TableHead className="sticky top-0 z-20 bg-emerald-900 text-right text-white">
                  <TituloOrdenable titulo="m² Const." idx={6} orden={orden} onOrdenar={alOrdenar} />
                </TableHead>
                <TableHead className="sticky top-0 z-20 bg-emerald-900 text-right text-white">
                  <TituloOrdenable titulo="Costo/m²" idx={7} orden={orden} onOrdenar={alOrdenar} />
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filas}
              <TableRow className="border-t-2 border-zinc-300 bg-zinc-100 font-bold hover:bg-zinc-100">
                <TableCell className="sticky left-0 z-10 bg-zinc-100 text-xs">Gran Total</TableCell>
                {celdasMetricas(
                  totalAgg.presupuesto,
                  pctDe(totalAgg),
                  totalAgg.costoAvance,
                  totalAgg.costoDir,
                  desvDe(totalAgg),
                  totalAgg.area,
                  totalAgg.m2c,
                )}
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {modalPartida && (
        <ModalPartida
          worksNo={modalPartida.worksNo}
          taskNo={modalPartida.taskNo}
          tareaDisplay={modalPartida.tareaDisplay}
          onClose={() => setModalPartida(null)}
        />
      )}
    </div>
  );
}
